(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else if (root.document && !root.RealmGallery) root.RealmGallery = factory(root.document, root);
})(typeof window === 'undefined' ? globalThis : window, function (document, window) {
  const mounted = new WeakMap(), active = new Set();
  const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  class ImageRetryController {
    constructor(stage, slides) {
      this.listeners = []; this.alerts = [];
      if (typeof document.createElement !== 'function') return;
      for (const slide of slides) {
        const image = slide.querySelector('img'); if (!image) continue;
        image.draggable = false;
        const alert = document.createElement('div'); alert.className = 'realm-gallery-image-error'; alert.hidden = true; alert.setAttribute('role', 'status');
        const text = document.createElement('span'); text.textContent = stage.dataset.imageError;
        const retry = document.createElement('button'); retry.className = 'realm-gallery-image-retry swiper-no-swiping'; retry.type = 'button'; retry.textContent = stage.dataset.imageRetry;
        alert.append(text, retry); slide.append(alert); this.alerts.push(alert);
        let timer = null, restoreFocus = false;
        const clearTimer = () => { if (timer !== null) window.clearTimeout?.(timer); timer = null; };
        const settle = failed => {
          clearTimer(); retry.disabled = false; alert.setAttribute('aria-busy', 'false');
          text.textContent = stage.dataset.imageError; alert.hidden = !failed; slide.dataset.imageFailed = String(failed);
          if (restoreFocus && (document.activeElement === retry || document.activeElement === document.body)) {
            if (failed && !slide.inert) retry.focus?.({ preventScroll: true });
            else if (!failed && !slide.inert) stage.closest('.realm-product-gallery')?.querySelector('[data-gallery-index][aria-pressed="true"]')?.focus?.({ preventScroll: true });
          }
          restoreFocus = false;
        };
        const sync = () => settle(image.complete && image.naturalWidth === 0);
        this.listen(image, 'load', sync);
        this.listen(image, 'error', () => settle(true));
        this.listen(retry, 'click', event => {
          event.preventDefault(); event.stopPropagation();
          const source = image.getAttribute('src'); if (!source || retry.disabled) return;
          const sourceSet = image.getAttribute('srcset');
          restoreFocus = document.activeElement === retry;
          clearTimer(); retry.disabled = true; alert.hidden = false; alert.setAttribute('aria-busy', 'true');
          text.textContent = stage.dataset.imageLoading || stage.dataset.imageRetry;
          image.removeAttribute('srcset'); image.removeAttribute('src'); image.setAttribute('src', source);
          if (sourceSet) image.setAttribute('srcset', sourceSet);
          if (typeof window.setTimeout === 'function') timer = window.setTimeout(() => settle(true), 12000);
        });
        this.listeners.push(clearTimer);
        if (image.complete) sync();
      }
    }
    listen(target, type, callback) { target.addEventListener?.(type, callback); this.listeners.push(() => target.removeEventListener?.(type, callback)); }
    destroy() { this.listeners.forEach(remove => remove()); this.alerts.forEach(node => node.remove()); this.listeners = []; this.alerts = []; }
  }
  function measureDelivery(entry) {
    const width = Number(entry.stage.getBoundingClientRect?.().width);
    if (width > 0) {
      for (const image of entry.stage.querySelectorAll('.realm-gallery-slide>img')) image.setAttribute('sizes', `${Math.ceil(width)}px`);
    }
    for (const image of entry.gallery.querySelectorAll('.realm-gallery-thumbnails img')) {
      const thumbWidth = Number(image.getBoundingClientRect?.().width);
      if (thumbWidth > 0) image.setAttribute('sizes', `${Math.ceil(thumbWidth)}px`);
    }
  }
  function observeDelivery(entry) {
    measureDelivery(entry);
    if (typeof window.ResizeObserver === 'function') {
      entry.deliveryObserver = new window.ResizeObserver(() => measureDelivery(entry));
      entry.deliveryObserver.observe(entry.stage);
    }
    return entry;
  }
  function galleryHistory(stage, owner, slides) {
    const productId = stage.dataset.galleryProductId, themeId = String(window.RealmTheme?.contractContext?.theme_id || '');
    const key = `${productId}:${owner.id}`;
    const url = () => window.location?.pathname + window.location?.search;
    const imageId = index => slides[index]?.dataset.galleryImageId;
    const enabled = /^[1-9]\d*$/.test(productId || '') && /^[1-9]\d*$/.test(themeId) && Boolean(owner.id && window.location && window.history?.replaceState);
    const object = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    function read(event = {}) {
      if (!enabled) return null;
      try {
        const navigation = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
        if (!event.persisted && navigation !== 'back_forward' && navigation !== 'reload') return null;
        const saved = window.history.state?.realmProductGalleries?.[key];
        if (saved?.version !== 1 || saved.productId !== productId || saved.themeId !== themeId || saved.url !== url() || !/^[1-9]\d*$/.test(saved.imageId || '')) return null;
        const matches = slides.map((_, index) => imageId(index) === saved.imageId ? index : -1).filter(index => index >= 0);
        return matches.length === 1 ? matches[0] : null;
      } catch (_) { return null; }
    }
    function remember(index) {
      if (!enabled || !Number.isInteger(index) || !/^[1-9]\d*$/.test(imageId(index) || '')) return false;
      try {
        const prior = object(window.history.state);
        const saved = { version: 1, productId, themeId, url: url(), imageId: imageId(index) };
        window.history.replaceState({ ...prior, realmProductGalleries: { ...object(prior.realmProductGalleries), [key]: saved } }, '', window.location.href);
        return true;
      } catch (_) { return false; }
    }
    return { read, remember };
  }
  function mount(stage) {
    if (mounted.has(stage)) return mounted.get(stage);
    const owner = stage.closest('[data-realm-product]'), gallery = stage.closest('.realm-product-gallery');
    const slides = Array.from(stage.querySelectorAll('.realm-gallery-slide'));
    if (!owner || !gallery || !slides.length) return null;
    const imageRetry = new ImageRetryController(stage, slides);
    if (typeof window.Swiper !== 'function') {
      const entry = observeDelivery({ stage, gallery, imageRetry, swiper: null, sync: () => {}, click: null });
      mounted.set(stage, entry); active.add(entry); return entry;
    }
    const previous = stage.querySelector('[data-gallery-step="-1"]'), next = stage.querySelector('[data-gallery-step="1"]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-index]'));
    const history = galleryHistory(stage, owner, slides), initialSlide = history.read() ?? 0;
    const sync = swiper => {
      const index = Math.max(0, Math.min(slides.length - 1, Number(swiper.activeIndex) || 0));
      owner.dataset.galleryActiveIndex = String(index);
      slides.forEach((slide, number) => { const hidden = number !== index; slide.setAttribute('aria-hidden', String(hidden)); slide.inert = hidden; });
      thumbs.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.galleryIndex) === index)));
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      owner.querySelectorAll('[data-gallery-count]').forEach(node => { node.textContent = `${index + 1} // ${slides.length}`; });
    };
    let swiper;
    try {
      swiper = new window.Swiper(stage, {
        initialSlide,
        slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 0,
        effect: 'fade', fadeEffect: { crossFade: false }, speed: preference?.matches ? 0 : 300,
        loop: false, rewind: false, autoHeight: true,
        allowTouchMove: true, simulateTouch: true, followFinger: true, touchStartPreventDefault: false,
        threshold: 5, touchAngle: 45, shortSwipes: true, longSwipes: true,
        longSwipesMs: 300, longSwipesRatio: .5, preventInteractionOnTransition: false,
        navigation: { prevEl: previous, nextEl: next },
        a11y: { enabled: true, prevSlideMessage: previous?.getAttribute('aria-label') || '', nextSlideMessage: next?.getAttribute('aria-label') || '' },
        on: { init() { sync(this); }, slideChange() { sync(this); history.remember(this.activeIndex); } }
      });
    } catch (_) { imageRetry.destroy(); return null; }
    const click = event => {
      const button = event.target.closest?.('[data-gallery-index], [data-gallery-step]');
      if (!button || !gallery.contains(button)) return;
      event.preventDefault(); event.stopPropagation();
      if (button.disabled || swiper.destroyed) return;
      if (button.hasAttribute('data-gallery-index')) {
        const index = Number(button.dataset.galleryIndex);
        if (Number.isInteger(index) && index >= 0 && index < slides.length) swiper.slideTo(index);
      } else if (button.dataset.galleryStep === '-1') swiper.slidePrev();
      else if (button.dataset.galleryStep === '1') swiper.slideNext();
    };
    gallery.addEventListener('click', click, true);
    const restore = event => {
      const index = history.read(event);
      if (index !== null && !swiper.destroyed) { swiper.slideTo(index, 0, false); sync(swiper); }
    };
    const entry = observeDelivery({ stage, gallery, swiper, sync, click, imageRetry, history, restore });
    mounted.set(stage, entry); active.add(entry); sync(swiper);
    return entry;
  }
  function destroy(entry) {
    if (entry.click) entry.gallery.removeEventListener('click', entry.click, true);
    entry.deliveryObserver?.disconnect();
    entry.imageRetry?.destroy(); entry.swiper?.destroy(true, true); mounted.delete(entry.stage); active.delete(entry);
  }
  function mountWithin(scope = document) {
    for (const entry of active) if (entry.stage.isConnected === false) destroy(entry);
    if (scope.matches?.('[data-realm-gallery]')) mount(scope);
    scope.querySelectorAll('[data-realm-gallery]').forEach(mount);
  }
  preference?.addEventListener?.('change', () => {
    for (const entry of active) {
      if (!entry.swiper || entry.swiper.destroyed) continue;
      entry.swiper.params.speed = preference.matches ? 0 : 300;
      if (preference.matches) {
        entry.swiper.setTransition(0);
        entry.swiper.slideTo(entry.swiper.activeIndex, 0, false);
        entry.sync(entry.swiper);
      }
    }
  });
  window.addEventListener?.('resize', () => { for (const entry of active) measureDelivery(entry); });
  window.addEventListener?.('pagehide', () => { for (const entry of active) if (entry.swiper && !entry.swiper.destroyed) entry.history.remember(entry.swiper.activeIndex); });
  window.addEventListener?.('pageshow', event => { for (const entry of active) { entry.restore?.(event); measureDelivery(entry); } });
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const entry of active) if (event.target.contains(entry.stage)) destroy(entry);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountWithin(), { once: true });
  else mountWithin();
  return { mount, mountWithin, mounted, active, ImageRetryController, measureDelivery };
});
